import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import './NumberInput.css';
import { useI18n } from '../../i18n/I18nContext';

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  className?: string;
  inputClassName?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  disabled?: boolean;
}

function clampValue(value: number, min?: number, max?: number) {
  let nextValue = Number.isNaN(value) ? (min ?? 0) : value;
  if (typeof min === 'number') {
    nextValue = Math.max(min, nextValue);
  }
  if (typeof max === 'number') {
    nextValue = Math.min(max, nextValue);
  }
  return nextValue;
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  className = '',
  inputClassName = '',
  onKeyDown,
  disabled = false,
}) => {
  const { t } = useI18n();

  const handleDecrement = () => {
    if (disabled) {
      return;
    }
    onChange(clampValue(value - 1, min, max));
  };
  const handleIncrement = () => {
    if (disabled) {
      return;
    }
    onChange(clampValue(value + 1, min, max));
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    onChange(clampValue(Number(event.target.value), min, max));
  };

  return (
    <div className={`number-input ${className}`.trim()}>
      <button
        type="button"
        className="number-input-button"
        onClick={handleDecrement}
        disabled={disabled}
        aria-label={t('table.decreaseQuantity')}
        title={t('table.decreaseQuantity')}
      >
        <FontAwesomeIcon icon={faMinus} />
      </button>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={handleInputChange}
        onKeyDown={onKeyDown}
        className={inputClassName}
        disabled={disabled}
      />
      <button
        type="button"
        className="number-input-button"
        onClick={handleIncrement}
        disabled={disabled}
        aria-label={t('table.increaseQuantity')}
        title={t('table.increaseQuantity')}
      >
        <FontAwesomeIcon icon={faPlus} />
      </button>
    </div>
  );
};

export default NumberInput;
